/**
 * Documentation Hub - WNC
 *
 * Getting started guides, API docs, and FAQs for merchants and developers.
 * Mostly identical to main website — universal documentation.
 */

import React, { useState } from 'react';
import { Card, Badge, Input, Footer, GradientHero, SEO, breadcrumbSchema } from '@coupling/ui';
import Header from '../components/Header';
import { seoConfig } from '../config/seo';

export const Docs: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const docCategories = [
    {
      title: 'Getting Started',
      icon: '🚀',
      description: 'Quick start guides for WNC merchants and customers',
      docs: [
        {
          title: 'Merchant Quick Start',
          slug: 'merchant-quickstart',
          description: 'Set up your account and issue your first tokens in 5 minutes',
          difficulty: 'Beginner',
        },
        {
          title: 'Customer Wallet Setup',
          slug: 'customer-wallet',
          description: 'Download the app and start managing your tokens',
          difficulty: 'Beginner',
        },
        {
          title: 'Understanding Token Economics',
          slug: 'token-economics',
          description: 'Learn how tokens work, exchange rates, and value proposition',
          difficulty: 'Intermediate',
        },
      ],
    },
    {
      title: 'Merchant Guides',
      icon: '🏪',
      description: 'Detailed guides for running your rewards program',
      docs: [
        {
          title: 'Creating Your First Token',
          slug: 'create-token',
          description: 'Step-by-step guide to token creation and configuration',
          difficulty: 'Beginner',
        },
        {
          title: 'Issuing Tokens to Customers',
          slug: 'issue-tokens',
          description: 'Methods for distributing tokens: QR codes, links, and bulk issuance',
          difficulty: 'Beginner',
        },
        {
          title: 'Accepting Token Payments',
          slug: 'accept-tokens',
          description: 'Set up your POS system to accept tokens as payment',
          difficulty: 'Intermediate',
        },
        {
          title: 'Analytics & Reporting',
          slug: 'analytics',
          description: 'Track token circulation, redemptions, and customer engagement',
          difficulty: 'Intermediate',
        },
        {
          title: 'Partnership Networks',
          slug: 'partnerships',
          description: 'Create or join merchant networks to share token acceptance',
          difficulty: 'Advanced',
        },
      ],
    },
    {
      title: 'API Documentation',
      icon: '⚙️',
      description: 'For developers integrating Coupling',
      docs: [
        {
          title: 'Authentication',
          slug: 'api-auth',
          description: 'API keys, OAuth flows, and security best practices',
          difficulty: 'Advanced',
        },
        {
          title: 'Token Management API',
          slug: 'api-tokens',
          description: 'Create, issue, and manage tokens programmatically',
          difficulty: 'Advanced',
        },
        {
          title: 'Webhooks',
          slug: 'api-webhooks',
          description: 'Real-time notifications for token events',
          difficulty: 'Advanced',
        },
        {
          title: 'Rate Limits & Quotas',
          slug: 'api-limits',
          description: 'Understanding API usage limits and best practices',
          difficulty: 'Advanced',
        },
      ],
    },
    {
      title: 'Troubleshooting',
      icon: '🔧',
      description: 'Common issues and solutions',
      docs: [
        {
          title: 'Token Not Showing Up',
          slug: 'troubleshoot-tokens',
          description: 'Debugging token issuance and delivery issues',
          difficulty: 'Beginner',
        },
        {
          title: 'Payment Processing Errors',
          slug: 'troubleshoot-payments',
          description: 'Resolving token redemption and acceptance problems',
          difficulty: 'Intermediate',
        },
        {
          title: 'Account & Security Issues',
          slug: 'troubleshoot-security',
          description: 'Login problems, password resets, and security concerns',
          difficulty: 'Beginner',
        },
      ],
    },
  ];

  const popularDocs = [
    { title: 'Merchant Quick Start', slug: 'merchant-quickstart', views: '12.5K' },
    { title: 'Creating Your First Token', slug: 'create-token', views: '8.2K' },
    { title: 'Partnership Networks', slug: 'partnerships', views: '6.1K' },
    { title: 'API Authentication', slug: 'api-auth', views: '4.8K' },
  ];

  const filteredCategories = docCategories.map(category => ({
    ...category,
    docs: category.docs.filter(doc =>
      searchQuery === '' ||
      doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.description.toLowerCase().includes(searchQuery.toLowerCase())
    ),
  })).filter(category => category.docs.length > 0);

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Beginner':
        return 'success';
      case 'Intermediate':
        return 'warning';
      case 'Advanced':
        return 'error';
      default:
        return 'neutral';
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`Documentation - ${seoConfig.siteName}`}
        description="Getting started guides, API docs, and FAQs for WNC merchants and developers using Coupling."
        canonical={`${seoConfig.siteUrl}/docs`}
        ogImage={seoConfig.defaultOgImage}
        ogUrl={`${seoConfig.siteUrl}/docs`}
        jsonLd={breadcrumbSchema([
          { name: 'Home', url: seoConfig.siteUrl },
          { name: 'Documentation', url: `${seoConfig.siteUrl}/docs` },
        ])}
      />
      <Header />

      <GradientHero
        title="Documentation"
        description="Everything you need to know about using Coupling for your WNC business."
        className="py-16"
      >
        <div className="max-w-2xl mx-auto mt-8">
          <Input
            type="search"
            placeholder="Search documentation..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-white"
          />
        </div>
      </GradientHero>

      {/* Quick Links */}
      <section className="py-12 px-4 -mt-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-4">
            <Card className="p-6 text-center hover:shadow-lg transition-shadow cursor-pointer">
              <div className="text-3xl mb-2">📖</div>
              <h3 className="font-semibold text-neutral-900">Guides</h3>
              <p className="text-sm text-neutral-600 mt-1">Step-by-step tutorials</p>
            </Card>
            <Card className="p-6 text-center hover:shadow-lg transition-shadow cursor-pointer">
              <div className="text-3xl mb-2">📚</div>
              <h3 className="font-semibold text-neutral-900">API Docs</h3>
              <p className="text-sm text-neutral-600 mt-1">Developer reference</p>
            </Card>
            <Card className="p-6 text-center hover:shadow-lg transition-shadow cursor-pointer">
              <div className="text-3xl mb-2">❓</div>
              <h3 className="font-semibold text-neutral-900">FAQ</h3>
              <p className="text-sm text-neutral-600 mt-1">Common questions</p>
            </Card>
            <Card className="p-6 text-center hover:shadow-lg transition-shadow cursor-pointer">
              <div className="text-3xl mb-2">💬</div>
              <h3 className="font-semibold text-neutral-900">Support</h3>
              <p className="text-sm text-neutral-600 mt-1">Get help from our team</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Popular Docs & Featured Content */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <h2 className="text-3xl font-bold text-neutral-900 mb-8">Browse by Category</h2>

            <div className="space-y-12">
              {filteredCategories.map((category) => (
                <div key={category.title}>
                  <div className="flex items-center gap-3 mb-6">
                    <span className="text-4xl">{category.icon}</span>
                    <div>
                      <h3 className="text-2xl font-bold text-neutral-900">{category.title}</h3>
                      <p className="text-neutral-600">{category.description}</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {category.docs.map((doc) => (
                      <Card
                        key={doc.slug}
                        className="p-6 hover:shadow-md transition-shadow cursor-pointer"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-grow">
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className="text-lg font-semibold text-neutral-900">{doc.title}</h4>
                              <Badge
                                variant={getDifficultyColor(doc.difficulty) as any}
                                size="sm"
                              >
                                {doc.difficulty}
                              </Badge>
                            </div>
                            <p className="text-neutral-600">{doc.description}</p>
                          </div>
                          <svg
                            className="w-5 h-5 text-neutral-400 flex-shrink-0 mt-1"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Popular Docs */}
            <Card className="p-6">
              <h3 className="text-xl font-bold text-neutral-900 mb-4">Popular Docs</h3>
              <div className="space-y-3">
                {popularDocs.map((doc) => (
                  <div
                    key={doc.slug}
                    className="flex items-start justify-between gap-3 p-3 rounded-lg hover:bg-neutral-50 cursor-pointer transition-colors"
                  >
                    <div className="flex-grow">
                      <p className="font-medium text-neutral-900 text-sm">{doc.title}</p>
                      <p className="text-xs text-neutral-500">{doc.views} views</p>
                    </div>
                    <svg
                      className="w-4 h-4 text-neutral-400 flex-shrink-0 mt-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                ))}
              </div>
            </Card>

            {/* Help Card */}
            <Card className="p-6 bg-brand-primary text-white">
              <h3 className="text-xl font-bold mb-3">Need More Help?</h3>
              <p className="text-white/90 mb-4">
                Can't find what you're looking for? Our support team is here to help WNC merchants get started.
              </p>
              <button className="w-full px-4 py-2 bg-white text-brand-primary rounded-lg font-medium hover:bg-white/90 transition-colors">
                Contact Support
              </button>
            </Card>

            {/* Video Tutorials */}
            <Card className="p-6">
              <h3 className="text-xl font-bold text-neutral-900 mb-3">Video Tutorials</h3>
              <p className="text-neutral-600 text-sm mb-4">
                Watch step-by-step video guides for common tasks.
              </p>
              <div className="aspect-video bg-neutral-200 rounded-lg flex items-center justify-center">
                <svg
                  className="w-16 h-16 text-neutral-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </Card>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Docs;
